import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import {
  LogOut, Lock, Calendar, Users, Phone, Mail, Clock, Trash2, Check, X, RefreshCw,
  Image as ImageIcon, Star, Ticket, PartyPopper, Upload, Pencil, Gift, Utensils, BarChart3, Save, Plus, FileText, Palmtree,
} from "lucide-react";
import { api } from "../lib/api";
import { MENU, RESTAURANT, HOURS } from "../data";
import { translations } from "../i18n";

const BACKEND = process.env.REACT_APP_BACKEND_URL;
const fullUrl = (u) => (u && u.startsWith("http") ? u : `${BACKEND}${u}`);

const STATUS = {
  pending: { label: "Offen", cls: "bg-amber-100 text-amber-800 border-amber-300" },
  confirmed: { label: "Bestätigt", cls: "bg-green-100 text-green-800 border-green-300" },
  cancelled: { label: "Storniert", cls: "bg-red-100 text-red-700 border-red-300" },
};

function Login({ onLogin }) {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");
    try {
      const { data } = await api.post("/auth/login", { email, password });
      localStorage.setItem("qs_token", data.access_token);
      onLogin(data.user);
    } catch (err) {
      const d = err.response?.data?.detail;
      setError(typeof d === "string" ? d : "Anmeldung fehlgeschlagen");
    } finally {
      setLoading(false);
    }
  };
  const inputCls = "w-full bg-qs-bg border border-qs-border rounded-lg px-4 py-3 font-outfit text-qs-text focus:outline-none focus:border-qs-gold transition-colors";
  return (
    <div className="min-h-screen flex items-center justify-center bg-black px-5">
      <form onSubmit={submit} data-testid="admin-login-form" className="bg-qs-bg w-full max-w-sm p-8 shadow-2xl rounded-xl">
        <div className="flex flex-col items-center mb-6">
          <div className="w-12 h-12 rounded-full bg-qs-red flex items-center justify-center mb-3"><Lock className="text-white" size={20} /></div>
          <span className="font-script text-2xl text-qs-gold">Quattro Stelle</span>
          <p className="font-outfit text-xs tracking-widest uppercase text-qs-text2 mt-1">Kontrollzentrum</p>
        </div>
        <div className="space-y-4">
          <input data-testid="admin-email" type="email" required placeholder="E-Mail" value={email} onChange={(e) => setEmail(e.target.value)} className={inputCls} />
          <input data-testid="admin-password" type="password" required placeholder="Passwort" value={password} onChange={(e) => setPassword(e.target.value)} className={inputCls} />
        </div>
        {error && <p data-testid="admin-login-error" className="text-qs-red text-sm mt-3 font-outfit">{error}</p>}
        <button data-testid="admin-login-btn" disabled={loading} className="mt-5 w-full bg-qs-red text-white rounded-full py-3 text-xs font-semibold tracking-widest uppercase hover:bg-qs-reddark transition-colors disabled:opacity-60">
          {loading ? "Anmelden..." : "Anmelden"}
        </button>
      </form>
    </div>
  );
}

/* ---------------- Reservations ---------------- */
function ReservationsTab() {
  const [items, setItems] = useState([]);
  const [filter, setFilter] = useState("all");
  const [loading, setLoading] = useState(false);
  const load = async () => {
    setLoading(true);
    try { setItems((await api.get("/reservations")).data); } catch { /* */ } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);
  const setStatus = async (id, status) => {
    try { const { data } = await api.patch(`/reservations/${id}`, { status }); setItems((p) => p.map((r) => (r.id === id ? data : r))); toast.success("Status aktualisiert"); }
    catch { toast.error("Fehler"); }
  };
  const remove = async (id) => {
    try { await api.delete(`/reservations/${id}`); setItems((p) => p.filter((r) => r.id !== id)); toast.success("Gelöscht"); }
    catch { toast.error("Fehler"); }
  };
  const counts = { all: items.length, pending: items.filter((r) => r.status === "pending").length, confirmed: items.filter((r) => r.status === "confirmed").length, cancelled: items.filter((r) => r.status === "cancelled").length };
  const filtered = filter === "all" ? items : items.filter((r) => r.status === filter);
  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <div className="grid grid-cols-4 gap-3 flex-1 max-w-2xl">
          {Object.keys(counts).map((k) => (
            <button key={k} data-testid={`admin-filter-${k}`} onClick={() => setFilter(k)}
              className={`p-3 text-left border rounded-lg transition-colors ${filter === k ? "bg-qs-red text-white border-qs-red" : "bg-qs-bg border-qs-border hover:border-qs-gold"}`}>
              <p className="font-cormorant text-2xl">{counts[k]}</p>
              <p className="font-outfit text-[10px] uppercase tracking-widest mt-1">{k === "all" ? "Gesamt" : STATUS[k].label}</p>
            </button>
          ))}
        </div>
        <button data-testid="admin-refresh" onClick={load} className="ml-3 flex items-center gap-2 text-xs uppercase tracking-widest px-3 py-2 border border-qs-border rounded-full hover:border-qs-gold"><RefreshCw size={14} className={loading ? "animate-spin" : ""} /></button>
      </div>
      {filtered.length === 0 ? <div data-testid="admin-empty" className="text-center py-16 font-outfit text-qs-text2">Keine Reservierungen.</div> : (
        <div className="space-y-4">
          {filtered.map((r) => (
            <div key={r.id} data-testid={`admin-reservation-${r.id}`} className="bg-qs-bg border border-qs-border rounded-lg p-5">
              <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                <div className="flex-1">
                  <div className="flex items-center gap-3 mb-2">
                    <h3 className="font-cormorant text-2xl text-qs-text">{r.name}</h3>
                    <span className={`text-[10px] font-semibold uppercase tracking-widest border px-2 py-0.5 rounded-full ${STATUS[r.status]?.cls || ""}`}>{STATUS[r.status]?.label || r.status}</span>
                  </div>
                  <div className="grid sm:grid-cols-2 gap-x-8 gap-y-1.5 font-outfit text-sm text-qs-text2">
                    <span className="flex items-center gap-2"><Calendar size={14} className="text-qs-gold" /> {r.date}</span>
                    <span className="flex items-center gap-2"><Clock size={14} className="text-qs-gold" /> {r.time} Uhr</span>
                    <span className="flex items-center gap-2"><Users size={14} className="text-qs-gold" /> {r.guests} Personen</span>
                    <a href={`tel:${r.phone}`} className="flex items-center gap-2 hover:text-qs-red"><Phone size={14} className="text-qs-gold" /> {r.phone}</a>
                    <a href={`mailto:${r.email}`} className="flex items-center gap-2 hover:text-qs-red break-all"><Mail size={14} className="text-qs-gold" /> {r.email}</a>
                  </div>
                  {r.message && <p className="font-outfit text-sm text-qs-text2 mt-3 italic border-l-2 border-qs-gold pl-3">"{r.message}"</p>}
                </div>
                <div className="flex md:flex-col gap-2 shrink-0">
                  <button data-testid={`confirm-${r.id}`} onClick={() => setStatus(r.id, "confirmed")} className="flex items-center justify-center gap-1.5 text-xs uppercase tracking-wide px-3 py-2 rounded-full bg-green-600 text-white hover:bg-green-700"><Check size={14} /> Bestätigen</button>
                  <button data-testid={`cancel-${r.id}`} onClick={() => setStatus(r.id, "cancelled")} className="flex items-center justify-center gap-1.5 text-xs uppercase tracking-wide px-3 py-2 rounded-full border border-qs-border hover:bg-qs-bg2"><X size={14} /> Stornieren</button>
                  <button data-testid={`delete-${r.id}`} onClick={() => remove(r.id)} className="flex items-center justify-center gap-1.5 text-xs uppercase tracking-wide px-3 py-2 rounded-full text-qs-red hover:bg-qs-red/10"><Trash2 size={14} /></button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

/* ---------------- Vouchers ---------------- */
function VouchersTab() {
  const [items, setItems] = useState([]);
  useEffect(() => { api.get("/admin/vouchers").then(({ data }) => setItems(data)).catch(() => {}); }, []);
  const paid = items.filter((v) => v.status === "paid");
  const total = paid.reduce((s, v) => s + (v.amount || 0), 0);
  return (
    <div>
      <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 mb-6">
        <div className="bg-qs-bg border border-qs-border rounded-lg p-4"><p className="font-cormorant text-3xl text-qs-text">{paid.length}</p><p className="font-outfit text-[10px] uppercase tracking-widest text-qs-text2">Verkauft</p></div>
        <div className="bg-qs-bg border border-qs-border rounded-lg p-4"><p className="font-cormorant text-3xl text-qs-gold">{total} €</p><p className="font-outfit text-[10px] uppercase tracking-widest text-qs-text2">Umsatz</p></div>
        <div className="bg-qs-bg border border-qs-border rounded-lg p-4"><p className="font-cormorant text-3xl text-qs-text">{items.length - paid.length}</p><p className="font-outfit text-[10px] uppercase tracking-widest text-qs-text2">Offen/Abgebrochen</p></div>
      </div>
      {paid.length === 0 ? <div data-testid="admin-vouchers-empty" className="text-center py-16 font-outfit text-qs-text2">Noch keine verkauften Gutscheine.</div> : (
        <div className="overflow-x-auto">
          <table className="w-full text-left font-outfit text-sm" data-testid="admin-vouchers-table">
            <thead><tr className="text-qs-text2 text-xs uppercase tracking-widest border-b border-qs-border">
              <th className="py-3 pr-4">Code</th><th className="py-3 pr-4">Betrag</th><th className="py-3 pr-4">Design</th><th className="py-3 pr-4">Käufer</th><th className="py-3">Datum</th>
            </tr></thead>
            <tbody>
              {paid.map((v) => (
                <tr key={v.id} data-testid={`voucher-row-${v.code}`} className="border-b border-qs-border/50 text-qs-text">
                  <td className="py-3 pr-4 font-semibold text-qs-gold tracking-wide">{v.code}</td>
                  <td className="py-3 pr-4">{v.amount} €</td>
                  <td className="py-3 pr-4 capitalize">{v.design}</td>
                  <td className="py-3 pr-4">{v.buyer_name}<br /><span className="text-qs-text2 text-xs">{v.buyer_email}</span></td>
                  <td className="py-3 text-qs-text2 text-xs">{(v.created_at || "").slice(0, 10)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

/* ---------------- Gallery ---------------- */
function GalleryTab() {
  const [items, setItems] = useState([]);
  const [altDe, setAltDe] = useState("");
  const [altEn, setAltEn] = useState("");
  const [file, setFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const load = () => api.get("/gallery").then(({ data }) => setItems(data)).catch(() => {});
  useEffect(() => { load(); }, []);
  const upload = async (e) => {
    e.preventDefault();
    if (!file) { toast.error("Bitte ein Bild wählen"); return; }
    setUploading(true);
    const fd = new FormData();
    fd.append("file", file); fd.append("alt_de", altDe); fd.append("alt_en", altEn);
    try {
      await api.post("/admin/gallery", fd);
      toast.success("Bild hochgeladen");
      setFile(null); setAltDe(""); setAltEn("");
      document.getElementById("gallery-file-input").value = "";
      load();
    } catch (err) { toast.error(err.response?.data?.detail || "Upload fehlgeschlagen"); }
    finally { setUploading(false); }
  };
  const remove = async (id) => {
    try { await api.delete(`/admin/gallery/${id}`); setItems((p) => p.filter((i) => i.id !== id)); toast.success("Bild entfernt"); }
    catch { toast.error("Fehler"); }
  };
  const inputCls = "w-full bg-qs-bg border border-qs-border rounded-lg px-3 py-2.5 font-outfit text-qs-text focus:outline-none focus:border-qs-gold";
  return (
    <div>
      <form onSubmit={upload} data-testid="gallery-upload-form" className="bg-qs-bg border border-qs-border rounded-xl p-5 mb-6">
        <h3 className="font-cormorant text-xl text-qs-text mb-3">Neues Bild hochladen</h3>
        <div className="grid sm:grid-cols-3 gap-3">
          <input id="gallery-file-input" data-testid="gallery-file" type="file" accept="image/*" onChange={(e) => setFile(e.target.files[0])} className={inputCls} />
          <input data-testid="gallery-alt-de" value={altDe} onChange={(e) => setAltDe(e.target.value)} placeholder="Bildtitel (DE)" className={inputCls} />
          <input data-testid="gallery-alt-en" value={altEn} onChange={(e) => setAltEn(e.target.value)} placeholder="Bildtitel (EN)" className={inputCls} />
        </div>
        <button data-testid="gallery-upload-btn" disabled={uploading} className="mt-4 inline-flex items-center gap-2 bg-qs-red text-white rounded-full px-6 py-2.5 text-xs font-semibold tracking-widest uppercase hover:bg-qs-reddark disabled:opacity-60">
          <Upload size={14} /> {uploading ? "Lädt..." : "Hochladen"}
        </button>
      </form>
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4">
        {items.map((img) => (
          <div key={img.id} data-testid={`gallery-item-${img.id}`} className="relative group rounded-lg overflow-hidden border border-qs-border">
            <img src={fullUrl(img.url)} alt={img.alt?.de} className="w-full h-40 object-cover" />
            <div className="absolute inset-x-0 bottom-0 bg-black/60 px-2 py-1"><p className="font-outfit text-white text-xs truncate">{img.alt?.de}</p></div>
            <button data-testid={`gallery-delete-${img.id}`} onClick={() => remove(img.id)} className="absolute top-2 right-2 w-8 h-8 rounded-full bg-qs-red text-white flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity"><Trash2 size={14} /></button>
          </div>
        ))}
      </div>
    </div>
  );
}

/* ---------------- Reviews ---------------- */
const emptyReview = { name: "", rating: 5, role_de: "", role_en: "", text_de: "", text_en: "" };
function ReviewsTab() {
  const [items, setItems] = useState([]);
  const [form, setForm] = useState(emptyReview);
  const [editId, setEditId] = useState(null);
  const load = () => api.get("/reviews").then(({ data }) => setItems(data)).catch(() => {});
  useEffect(() => { load(); }, []);
  const startEdit = (r) => { setEditId(r.id); setForm({ name: r.name, rating: r.rating, role_de: r.role.de, role_en: r.role.en, text_de: r.text.de, text_en: r.text.en }); window.scrollTo(0, 0); };
  const reset = () => { setEditId(null); setForm(emptyReview); };
  const save = async (e) => {
    e.preventDefault();
    try {
      if (editId) await api.put(`/admin/reviews/${editId}`, form);
      else await api.post("/admin/reviews", form);
      toast.success("Gespeichert"); reset(); load();
    } catch { toast.error("Fehler beim Speichern"); }
  };
  const remove = async (id) => { try { await api.delete(`/admin/reviews/${id}`); setItems((p) => p.filter((i) => i.id !== id)); toast.success("Gelöscht"); } catch { toast.error("Fehler"); } };
  const upd = (k) => (e) => setForm((p) => ({ ...p, [k]: e.target.value }));
  const inputCls = "w-full bg-qs-bg border border-qs-border rounded-lg px-3 py-2.5 font-outfit text-qs-text focus:outline-none focus:border-qs-gold";
  return (
    <div>
      <form onSubmit={save} data-testid="review-form" className="bg-qs-bg border border-qs-border rounded-xl p-5 mb-6">
        <h3 className="font-cormorant text-xl text-qs-text mb-3">{editId ? "Bewertung bearbeiten" : "Neue Bewertung"}</h3>
        <div className="grid sm:grid-cols-2 gap-3">
          <input data-testid="review-name" required value={form.name} onChange={upd("name")} placeholder="Name" className={inputCls} />
          <select data-testid="review-rating" value={form.rating} onChange={(e) => setForm((p) => ({ ...p, rating: parseInt(e.target.value, 10) }))} className={inputCls}>
            {[5, 4, 3, 2, 1].map((n) => <option key={n} value={n}>{n} Sterne</option>)}
          </select>
          <input data-testid="review-role-de" value={form.role_de} onChange={upd("role_de")} placeholder="Zeit/Rolle (DE) z.B. vor 2 Wochen" className={inputCls} />
          <input data-testid="review-role-en" value={form.role_en} onChange={upd("role_en")} placeholder="Time/role (EN) e.g. 2 weeks ago" className={inputCls} />
          <textarea data-testid="review-text-de" required rows={3} value={form.text_de} onChange={upd("text_de")} placeholder="Bewertungstext (DE)" className={inputCls} />
          <textarea data-testid="review-text-en" rows={3} value={form.text_en} onChange={upd("text_en")} placeholder="Review text (EN)" className={inputCls} />
        </div>
        <div className="flex gap-2 mt-4">
          <button data-testid="review-save-btn" className="inline-flex items-center gap-2 bg-qs-red text-white rounded-full px-6 py-2.5 text-xs font-semibold tracking-widest uppercase hover:bg-qs-reddark"><Check size={14} /> {editId ? "Aktualisieren" : "Hinzufügen"}</button>
          {editId && <button type="button" onClick={reset} className="inline-flex items-center rounded-full px-6 py-2.5 text-xs font-semibold tracking-widest uppercase border border-qs-border hover:border-qs-gold">Abbrechen</button>}
        </div>
      </form>
      <div className="space-y-3">
        {items.map((r) => (
          <div key={r.id} data-testid={`review-item-${r.id}`} className="bg-qs-bg border border-qs-border rounded-lg p-4 flex items-start justify-between gap-4">
            <div className="flex-1">
              <div className="flex items-center gap-2 mb-1">
                <span className="font-cormorant text-lg text-qs-text">{r.name}</span>
                <span className="flex">{[...Array(r.rating)].map((_, i) => <Star key={i} size={12} className="fill-qs-gold text-qs-gold" />)}</span>
                <span className="text-qs-text2 text-xs">{r.role?.de}</span>
              </div>
              <p className="font-outfit text-sm text-qs-text2 line-clamp-2 italic">"{r.text?.de}"</p>
            </div>
            <div className="flex gap-2 shrink-0">
              <button data-testid={`review-edit-${r.id}`} onClick={() => startEdit(r)} className="w-9 h-9 rounded-full border border-qs-border flex items-center justify-center hover:border-qs-gold text-qs-text"><Pencil size={14} /></button>
              <button data-testid={`review-delete-${r.id}`} onClick={() => remove(r.id)} className="w-9 h-9 rounded-full border border-qs-border flex items-center justify-center text-qs-red hover:bg-qs-red/10"><Trash2 size={14} /></button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

/* ---------------- Events ---------------- */
function EventsTab() {
  const [items, setItems] = useState([]);
  useEffect(() => { api.get("/admin/events").then(({ data }) => setItems(data)).catch(() => {}); }, []);
  return items.length === 0 ? <div data-testid="admin-events-empty" className="text-center py-16 font-outfit text-qs-text2">Noch keine Veranstaltungsanfragen.</div> : (
    <div className="space-y-4">
      {items.map((e, i) => (
        <div key={i} data-testid={`event-item-${i}`} className="bg-qs-bg border border-qs-border rounded-lg p-5">
          <div className="flex items-center gap-3 mb-2">
            <h3 className="font-cormorant text-xl text-qs-text">{e.name}</h3>
            <span className="text-[10px] font-semibold uppercase tracking-widest border border-qs-gold text-qs-gold rounded-full px-2 py-0.5">{e.occasion}</span>
          </div>
          <div className="grid sm:grid-cols-2 gap-x-8 gap-y-1.5 font-outfit text-sm text-qs-text2">
            <span className="flex items-center gap-2"><Calendar size={14} className="text-qs-gold" /> {e.date || "—"}</span>
            <span className="flex items-center gap-2"><Users size={14} className="text-qs-gold" /> {e.guests} Personen</span>
            <a href={`tel:${e.phone}`} className="flex items-center gap-2 hover:text-qs-red"><Phone size={14} className="text-qs-gold" /> {e.phone}</a>
            <a href={`mailto:${e.email}`} className="flex items-center gap-2 hover:text-qs-red break-all"><Mail size={14} className="text-qs-gold" /> {e.email}</a>
          </div>
          {e.message && <p className="font-outfit text-sm text-qs-text2 mt-3 italic border-l-2 border-qs-gold pl-3">"{e.message}"</p>}
        </div>
      ))}
    </div>
  );
}

/* ---------------- Menu editor ---------------- */
const clone = (o) => JSON.parse(JSON.stringify(o));
function MenuTab() {
  const [menu, setMenu] = useState(null);
  const [saving, setSaving] = useState(false);
  useEffect(() => {
    api.get("/menu").then(({ data }) => {
      setMenu(Array.isArray(data) && data.length ? data : clone(MENU));
    }).catch(() => setMenu(clone(MENU)));
  }, []);
  const save = async () => {
    setSaving(true);
    try { await api.put("/admin/menu", { menu }); toast.success("Speisekarte gespeichert"); }
    catch { toast.error("Speichern fehlgeschlagen"); }
    finally { setSaving(false); }
  };
  const editItem = (ci, gi, ii, field, sub, value) => {
    const nm = clone(menu);
    const arr = gi == null ? nm[ci].items : nm[ci].groups[gi].items;
    if (field === "desc") arr[ii].desc = { ...(arr[ii].desc || {}), [sub]: value };
    else arr[ii][field] = value;
    setMenu(nm);
  };
  const addItem = (ci, gi) => {
    const nm = clone(menu);
    const arr = gi == null ? nm[ci].items : nm[ci].groups[gi].items;
    arr.push({ name: "Neues Gericht", desc: { de: "", en: "" }, price: "0,00" });
    setMenu(nm);
  };
  const delItem = (ci, gi, ii) => {
    const nm = clone(menu);
    const arr = gi == null ? nm[ci].items : nm[ci].groups[gi].items;
    arr.splice(ii, 1);
    setMenu(nm);
  };
  if (!menu) return <div className="text-center py-16 font-outfit text-qs-text2">Laden...</div>;
  const inp = "bg-qs-bg border border-qs-border rounded px-2.5 py-1.5 font-outfit text-qs-text text-sm focus:outline-none focus:border-qs-gold";

  const ItemRow = ({ item, ci, gi, ii }) => (
    <div data-testid={`menu-edit-item-${ci}-${gi ?? "x"}-${ii}`} className="flex flex-col md:flex-row gap-2 items-start bg-qs-bg2 border border-qs-border rounded-lg p-3">
      <input className={`${inp} flex-1 w-full`} value={item.name} onChange={(e) => editItem(ci, gi, ii, "name", null, e.target.value)} placeholder="Name" />
      <input className={`${inp} md:w-40 w-full`} value={item.desc?.de || ""} onChange={(e) => editItem(ci, gi, ii, "desc", "de", e.target.value)} placeholder="Beschreibung DE" />
      <input className={`${inp} md:w-40 w-full`} value={item.desc?.en || ""} onChange={(e) => editItem(ci, gi, ii, "desc", "en", e.target.value)} placeholder="Description EN" />
      <input className={`${inp} md:w-20 w-full`} value={item.price || ""} onChange={(e) => editItem(ci, gi, ii, "price", null, e.target.value)} placeholder="Preis" />
      <button onClick={() => delItem(ci, gi, ii)} data-testid={`menu-del-${ci}-${gi ?? "x"}-${ii}`} className="w-9 h-9 shrink-0 rounded-full border border-qs-border flex items-center justify-center text-qs-red hover:bg-qs-red/10"><Trash2 size={14} /></button>
    </div>
  );

  return (
    <div className="pb-24">
      <div className="flex items-center justify-between mb-6 sticky top-[116px] bg-qs-bg2 py-3 z-[5]">
        <p className="font-outfit text-qs-text2 text-sm">Bearbeite Gerichte, Beschreibungen & Preise. Danach speichern.</p>
        <button data-testid="menu-save-btn" onClick={save} disabled={saving} className="inline-flex items-center gap-2 bg-qs-gold text-black rounded-full px-6 py-2.5 text-xs font-semibold tracking-widest uppercase hover:bg-qs-gold/90 disabled:opacity-60">
          <Save size={14} /> {saving ? "Speichert..." : "Speichern"}
        </button>
      </div>
      <div className="space-y-10">
        {menu.map((cat, ci) => (
          <div key={cat.id || ci} data-testid={`menu-cat-${cat.id}`}>
            <h3 className="font-cormorant text-2xl text-qs-gold mb-3">{cat.title?.de || cat.id}</h3>
            {cat.items && (
              <div className="space-y-2">
                {cat.items.map((item, ii) => <ItemRow key={ii} item={item} ci={ci} gi={null} ii={ii} />)}
                <button onClick={() => addItem(ci, null)} data-testid={`menu-add-${cat.id}`} className="inline-flex items-center gap-1.5 text-xs uppercase tracking-widest text-qs-gold hover:text-qs-text mt-1"><Plus size={14} /> Gericht hinzufügen</button>
              </div>
            )}
            {cat.groups && cat.groups.map((g, gi) => (
              <div key={gi} className="mt-4">
                <p className="font-cormorant text-lg text-qs-red mb-2">{g.label?.de}</p>
                <div className="space-y-2">
                  {g.items.map((item, ii) => <ItemRow key={ii} item={item} ci={ci} gi={gi} ii={ii} />)}
                  <button onClick={() => addItem(ci, gi)} className="inline-flex items-center gap-1.5 text-xs uppercase tracking-widest text-qs-gold hover:text-qs-text mt-1"><Plus size={14} /> Gericht hinzufügen</button>
                </div>
              </div>
            ))}
          </div>
        ))}
      </div>
    </div>
  );
}

/* ---------------- Stats ---------------- */
function StatsTab() {
  const [s, setS] = useState(null);
  useEffect(() => { api.get("/admin/stats").then(({ data }) => setS(data)).catch(() => {}); }, []);
  if (!s) return <div className="text-center py-16 font-outfit text-qs-text2">Laden...</div>;
  const maxRes = Math.max(1, ...s.reservations_by_month);
  const maxRev = Math.max(1, ...s.revenue_by_month);
  const Card = ({ label, value, tid }) => (
    <div data-testid={tid} className="bg-qs-bg border border-qs-border rounded-lg p-5">
      <p className="font-cormorant text-4xl text-qs-gold">{value}</p>
      <p className="font-outfit text-[10px] uppercase tracking-widest text-qs-text2 mt-1">{label}</p>
    </div>
  );
  const Chart = ({ title, values, max, color, fmt }) => (
    <div className="bg-qs-bg border border-qs-border rounded-lg p-5">
      <p className="font-cormorant text-xl text-qs-text mb-4">{title}</p>
      <div className="flex items-end gap-3 h-40">
        {values.map((val, i) => (
          <div key={i} className="flex-1 flex flex-col items-center justify-end h-full">
            <span className="font-outfit text-xs text-qs-text mb-1">{fmt ? fmt(val) : val}</span>
            <div className="w-full rounded-t transition-all" style={{ height: `${(val / max) * 100}%`, minHeight: val > 0 ? 4 : 0, background: color }} />
            <span className="font-outfit text-[10px] text-qs-text2 mt-2">{s.months[i].slice(5)}/{s.months[i].slice(2, 4)}</span>
          </div>
        ))}
      </div>
    </div>
  );
  return (
    <div className="space-y-6" data-testid="admin-stats">
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        <Card label="Reservierungen gesamt" value={s.totals.reservations} tid="stat-reservations" />
        <Card label="Gutscheine verkauft" value={s.totals.vouchers_sold} tid="stat-vouchers" />
        <Card label="Gutschein-Umsatz" value={`${s.totals.revenue} €`} tid="stat-revenue" />
        <Card label="Veranstaltungsanfragen" value={s.totals.events} tid="stat-events" />
      </div>
      <div className="grid lg:grid-cols-2 gap-4">
        <Chart title="Reservierungen pro Monat" values={s.reservations_by_month} max={maxRes} color="#8B2635" />
        <Chart title="Gutschein-Umsatz pro Monat" values={s.revenue_by_month} max={maxRev} color="#C5A059" fmt={(v) => `${v}€`} />
      </div>
    </div>
  );
}

/* ---------------- Content / Site settings ---------------- */
const DAY_KEYS = ["mon", "tue", "wed", "thu", "fri", "sat", "sun"];
const DAY_LABELS = { mon: "Montag", tue: "Dienstag", wed: "Mittwoch", thu: "Donnerstag", fri: "Freitag", sat: "Samstag", sun: "Sonntag" };
const CONTACT_FIELDS = [
  { key: "street", label: "Straße & Hausnummer" },
  { key: "city", label: "PLZ & Ort" },
  { key: "phone", label: "Telefon (Anzeige)" },
  { key: "phoneIntl", label: "Telefon (für Anruf-Link, z.B. +4982828900634)" },
  { key: "email", label: "E-Mail" },
  { key: "whatsapp", label: "WhatsApp-Nummer" },
];
const DEFAULT_VACATION_DE = "Wir machen Urlaub! In dieser Zeit bleibt unser Restaurant geschlossen. Wir freuen uns, Sie bald wieder bei uns begrüßen zu dürfen.";
const DEFAULT_VACATION_EN = "We're on holiday! Our restaurant is closed during this time. We look forward to welcoming you again soon.";

function SettingsTab() {
  const [contact, setContact] = useState(null);
  const [hours, setHours] = useState(null);
  const [about, setAbout] = useState(null);
  const [vacation, setVacation] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.get("/settings").then(({ data }) => {
      const s = data || {};
      const baseContact = {};
      CONTACT_FIELDS.forEach((f) => { baseContact[f.key] = (s.contact && s.contact[f.key]) ?? RESTAURANT[f.key] ?? ""; });
      setContact(baseContact);

      const srcHours = Array.isArray(s.hours) && s.hours.length ? s.hours : HOURS;
      const hoursMap = {};
      srcHours.forEach((d) => { hoursMap[d.key] = d; });
      setHours(DAY_KEYS.map((k) => {
        const d = hoursMap[k] || { key: k, closed: true };
        return { key: k, closed: !!d.closed, slotsText: (d.slots || []).join(", ") };
      }));

      const de = (s.about && s.about.de) || {};
      const en = (s.about && s.about.en) || {};
      setAbout({
        de: { p1: de.p1 || translations.de.about.p1, p2: de.p2 || translations.de.about.p2 },
        en: { p1: en.p1 || translations.en.about.p1, p2: en.p2 || translations.en.about.p2 },
      });

      const vac = s.vacation || {};
      setVacation({
        active: !!vac.active,
        message_de: vac.message_de || DEFAULT_VACATION_DE,
        message_en: vac.message_en || DEFAULT_VACATION_EN,
      });
    }).catch(() => toast.error("Einstellungen konnten nicht geladen werden"));
  }, []);

  const save = async () => {
    setSaving(true);
    try {
      const hoursPayload = hours.map((d) => d.closed
        ? { key: d.key, closed: true }
        : { key: d.key, slots: d.slotsText.split(",").map((s) => s.trim()).filter(Boolean) });
      await api.put("/admin/settings", { contact, hours: hoursPayload, about, vacation });
      toast.success("Inhalte gespeichert – auf der Website nach Neuladen sichtbar");
    } catch { toast.error("Speichern fehlgeschlagen"); }
    finally { setSaving(false); }
  };

  if (!contact || !hours || !about || !vacation) return <div className="text-center py-16 font-outfit text-qs-text2">Laden...</div>;
  const inp = "w-full bg-qs-bg border border-qs-border rounded-lg px-3 py-2.5 font-outfit text-qs-text text-sm focus:outline-none focus:border-qs-gold";

  return (
    <div className="pb-24 space-y-10" data-testid="admin-content-tab">
      <div className="flex items-center justify-between sticky top-[116px] bg-qs-bg2 py-3 z-[5]">
        <p className="font-outfit text-qs-text2 text-sm">Öffnungszeiten, Kontaktdaten & „Über uns"-Text bearbeiten.</p>
        <button data-testid="content-save-btn" onClick={save} disabled={saving} className="inline-flex items-center gap-2 bg-qs-gold text-black rounded-full px-6 py-2.5 text-xs font-semibold tracking-widest uppercase hover:bg-qs-gold/90 disabled:opacity-60">
          <Save size={14} /> {saving ? "Speichert..." : "Speichern"}
        </button>
      </div>

      {/* Vacation / Urlaubsmodus */}
      <div className={`rounded-xl border p-5 transition-colors ${vacation.active ? "border-qs-gold bg-qs-gold/5" : "border-qs-border bg-qs-bg"}`}>
        <div className="flex items-center justify-between gap-4">
          <div>
            <h3 className="font-cormorant text-2xl text-qs-gold flex items-center gap-2"><Palmtree size={20} /> Urlaubsmodus</h3>
            <p className="font-outfit text-qs-text2 text-sm mt-1">Wenn aktiviert, erscheint oben auf der Website ein Urlaubs-Hinweis.</p>
          </div>
          <button
            type="button"
            data-testid="vacation-toggle"
            onClick={() => setVacation((p) => ({ ...p, active: !p.active }))}
            className={`relative w-16 h-9 rounded-full transition-colors shrink-0 ${vacation.active ? "bg-qs-gold" : "bg-qs-border"}`}
            aria-pressed={vacation.active}
          >
            <span className={`absolute top-1 left-1 w-7 h-7 rounded-full bg-white shadow transition-transform ${vacation.active ? "translate-x-7" : ""}`} />
          </button>
        </div>
        <div className="grid lg:grid-cols-2 gap-4 mt-4">
          <div>
            <label className="block text-xs uppercase tracking-widest text-qs-text2 mb-1.5">Urlaubstext (Deutsch)</label>
            <textarea data-testid="vacation-message-de" rows={3} className={inp + " w-full"} value={vacation.message_de}
              onChange={(e) => setVacation((p) => ({ ...p, message_de: e.target.value }))} />
          </div>
          <div>
            <label className="block text-xs uppercase tracking-widest text-qs-text2 mb-1.5">Urlaubstext (Englisch)</label>
            <textarea data-testid="vacation-message-en" rows={3} className={inp + " w-full"} value={vacation.message_en}
              onChange={(e) => setVacation((p) => ({ ...p, message_en: e.target.value }))} />
          </div>
        </div>
        <p className="font-outfit text-qs-text2/70 text-xs mt-3">Nicht vergessen: Nach dem Umschalten „Speichern" klicken.</p>
      </div>

      {/* Contact */}
      <div>
        <h3 className="font-cormorant text-2xl text-qs-gold mb-4">Kontaktdaten</h3>
        <div className="grid sm:grid-cols-2 gap-4">
          {CONTACT_FIELDS.map((f) => (
            <div key={f.key}>
              <label className="block text-xs uppercase tracking-widest text-qs-text2 mb-1.5">{f.label}</label>
              <input data-testid={`content-contact-${f.key}`} className={inp} value={contact[f.key] || ""}
                onChange={(e) => setContact((p) => ({ ...p, [f.key]: e.target.value }))} />
            </div>
          ))}
        </div>
      </div>

      {/* Hours */}
      <div>
        <h3 className="font-cormorant text-2xl text-qs-gold mb-2">Öffnungszeiten</h3>
        <p className="font-outfit text-qs-text2 text-xs mb-4">Zeiträume mit Komma trennen, z.B. <span className="text-qs-text">11:30 – 14:00, 17:30 – 22:30</span>. Bei geschlossen den Haken setzen.</p>
        <div className="space-y-2">
          {hours.map((d, i) => (
            <div key={d.key} data-testid={`content-hours-${d.key}`} className="flex flex-col sm:flex-row sm:items-center gap-3 bg-qs-bg border border-qs-border rounded-lg p-3">
              <span className="font-cormorant text-lg text-qs-text w-32 shrink-0">{DAY_LABELS[d.key]}</span>
              <label className="flex items-center gap-2 text-xs uppercase tracking-widest text-qs-text2 shrink-0 cursor-pointer">
                <input type="checkbox" data-testid={`content-closed-${d.key}`} checked={d.closed}
                  onChange={(e) => setHours((p) => p.map((x, j) => j === i ? { ...x, closed: e.target.checked } : x))} />
                Geschlossen
              </label>
              <input data-testid={`content-slots-${d.key}`} className={`${inp} flex-1`} disabled={d.closed}
                placeholder="11:30 – 14:00, 17:30 – 22:30" value={d.slotsText}
                onChange={(e) => setHours((p) => p.map((x, j) => j === i ? { ...x, slotsText: e.target.value } : x))} />
            </div>
          ))}
        </div>
      </div>

      {/* About */}
      <div>
        <h3 className="font-cormorant text-2xl text-qs-gold mb-4">„Über uns"-Text</h3>
        <div className="grid lg:grid-cols-2 gap-6">
          {["de", "en"].map((l) => (
            <div key={l} className="space-y-3">
              <p className="font-outfit text-xs uppercase tracking-widest text-qs-text2">{l === "de" ? "Deutsch" : "Englisch"}</p>
              <textarea data-testid={`content-about-${l}-p1`} rows={4} className={inp} placeholder="Absatz 1" value={about[l].p1}
                onChange={(e) => setAbout((p) => ({ ...p, [l]: { ...p[l], p1: e.target.value } }))} />
              <textarea data-testid={`content-about-${l}-p2`} rows={4} className={inp} placeholder="Absatz 2" value={about[l].p2}
                onChange={(e) => setAbout((p) => ({ ...p, [l]: { ...p[l], p2: e.target.value } }))} />
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

const TABS = [
  { id: "reservations", label: "Reservierungen", icon: Calendar },
  { id: "menu", label: "Speisekarte", icon: Utensils },
  { id: "vouchers", label: "Gutscheine", icon: Ticket },
  { id: "gallery", label: "Galerie", icon: ImageIcon },
  { id: "reviews", label: "Bewertungen", icon: Star },
  { id: "events", label: "Veranstaltungen", icon: PartyPopper },
  { id: "content", label: "Inhalte", icon: FileText },
  { id: "stats", label: "Umsatz", icon: BarChart3 },
];

export default function AdminPage() {
  const navigate = useNavigate();
  const [authed, setAuthed] = useState(null);
  const [tab, setTab] = useState("reservations");

  useEffect(() => {
    const token = localStorage.getItem("qs_token");
    if (!token) { setAuthed(false); return; }
    api.get("/auth/me").then(() => setAuthed(true)).catch(() => { localStorage.removeItem("qs_token"); setAuthed(false); });
  }, []);

  const logout = () => { localStorage.removeItem("qs_token"); setAuthed(false); };

  if (authed === null) return <div className="min-h-screen flex items-center justify-center bg-black text-white font-outfit">Laden...</div>;
  if (!authed) return <Login onLogin={() => setAuthed(true)} />;

  return (
    <div className="min-h-screen bg-qs-bg2">
      <header className="bg-black text-white px-5 md:px-10 py-4 flex items-center justify-between sticky top-0 z-20 border-b border-qs-border">
        <div className="flex items-center gap-2">
          <Gift size={18} className="text-qs-gold" />
          <div>
            <span className="font-script text-xl text-qs-gold">Quattro Stelle</span>
            <p className="font-outfit text-[10px] tracking-widest uppercase text-white/60">Kontrollzentrum</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <button onClick={() => navigate("/")} className="text-xs uppercase tracking-widest px-3 py-2 border border-white/20 rounded-full hover:bg-white/10 hidden sm:block">Website</button>
          <button data-testid="admin-logout" onClick={logout} className="flex items-center gap-2 text-xs uppercase tracking-widest px-3 py-2 rounded-full bg-qs-red hover:bg-qs-reddark"><LogOut size={14} /> <span className="hidden sm:inline">Abmelden</span></button>
        </div>
      </header>

      {/* Tab bar */}
      <div className="bg-black/95 border-b border-qs-border sticky top-[68px] z-10 px-5 md:px-10 overflow-x-auto">
        <div className="flex gap-1 max-w-6xl mx-auto">
          {TABS.map((tb) => {
            const Icon = tb.icon;
            return (
              <button key={tb.id} data-testid={`admin-tab-${tb.id}`} onClick={() => setTab(tb.id)}
                className={`flex items-center gap-2 whitespace-nowrap px-4 py-3 text-xs font-semibold uppercase tracking-widest border-b-2 transition-colors ${tab === tb.id ? "border-qs-gold text-qs-gold" : "border-transparent text-white/60 hover:text-white"}`}>
                <Icon size={14} /> {tb.label}
              </button>
            );
          })}
        </div>
      </div>

      <div className="max-w-6xl mx-auto px-5 md:px-10 py-8">
        {tab === "reservations" && <ReservationsTab />}
        {tab === "menu" && <MenuTab />}
        {tab === "vouchers" && <VouchersTab />}
        {tab === "gallery" && <GalleryTab />}
        {tab === "reviews" && <ReviewsTab />}
        {tab === "events" && <EventsTab />}
        {tab === "content" && <SettingsTab />}
        {tab === "stats" && <StatsTab />}
      </div>
    </div>
  );
}
